import { IconCheck, IconPlus, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button, Radio, RadioGroup, TextInput } from '@cfa/react-core';

import useAuth from '@/hooks/useAuth';

import { cn } from '@/utils/tailwind';

import Constants from 'constants/index';
import { validateTextField } from 'utils/validateTextField';

import type { EditableQuizDetail } from '@/routes/TrainingPlans/BuildQuizzes/quizEditorTypes';

export interface BuildQuizzesProps {
  handlePassingGrade?: (grade?: number) => void;
  isEditMode?: boolean;
  moveQuestionDown?: (questionIndex: number) => void;
  moveQuestionUp?: (questionIndex: number) => void;
  onDeleteQuestion?: () => void;
  questionIndex: number;
  questionTemplateType?: { isMultipleChoiceTemplate: boolean };
  quiz: EditableQuizDetail;
  setQuiz: React.Dispatch<React.SetStateAction<EditableQuizDetail>>;
  setValidationMode: (validate: boolean) => void;
  totalQuestions?: number;
  validationMode: boolean;
}

const BuildQuizzesAnswers: React.FC<BuildQuizzesProps> = ({
  questionIndex,
  questionTemplateType,
  quiz,
  setQuiz,
  setValidationMode,
  validationMode,
}) => {
  const { t } = useTranslation();
  const { data: auth } = useAuth();
  const userLanguage = auth?.language ?? 'en';
  const selectedAnswerIndex = quiz.questions[questionIndex]?.answers.findIndex(
    answer => answer.correct,
  );

  const handleOptionText = (value: string, optionIndex: number) => {
    setQuiz(prevState => ({
      ...prevState,
      questions: prevState.questions.map((question, qi) =>
        qi === questionIndex
          ? {
              ...question,
              answers: question.answers.map((answer, ai) =>
                ai === optionIndex
                  ? {
                      ...answer,
                      answer: { ...answer.answer, [userLanguage]: value },
                    }
                  : answer,
              ),
            }
          : question,
      ),
    }));
  };

  const handleCorrectOption = (optionIndex: number) => {
    setQuiz(prevState => ({
      ...prevState,
      questions: prevState.questions.map((question, qi) =>
        qi === questionIndex
          ? {
              ...question,
              answers: question.answers.map((answer, ai) => ({
                ...answer,
                correct: ai === optionIndex,
              })),
            }
          : question,
      ),
    }));
  };

  const addOption = () => {
    setValidationMode(false);
    setQuiz(prevState => ({
      ...prevState,
      questions: prevState.questions.map((question, qi) =>
        qi === questionIndex
          ? {
              ...question,
              answers: [...question.answers, Constants.DEFAULT_QUIZ_ANSWER],
            }
          : question,
      ),
    }));
  };

  const deleteOption = (optionIndex: number) => {
    setQuiz(prevState => ({
      ...prevState,
      questions: prevState.questions.map((question, qi) =>
        qi === questionIndex
          ? {
              ...question,
              answers: question.answers.filter((_, ai) => ai !== optionIndex),
            }
          : question,
      ),
    }));
  };

  return (
    <div className="mx-0 my-4 text-left">
      <RadioGroup
        value={
          selectedAnswerIndex !== undefined && selectedAnswerIndex >= 0
            ? String(selectedAnswerIndex)
            : ''
        }
        orientation="vertical"
        onChange={value => handleCorrectOption(Number(value))}
        className="w-full!"
      >
        {quiz.questions[questionIndex]?.answers.map((option, optionIndex) => (
          <div
            className={cn(
              'mb-4 flex rounded border border-solid border-transparent px-1 pt-2 pb-3',
              {
                'border-feedback-success-base bg-feedback-success-container':
                  option.correct,
              },
            )}
            key={`questionOption-${optionIndex}`}
          >
            <Radio
              className={cn(
                'text-grayscale-gray4 [&::before]:border-1.5! [&::before]:border-grayscale-gray3! mt-4 flex cursor-pointer self-center pr-2 [&::before]:size-6 [&::before]:border-solid!',
                {
                  '[&::before]:border-feedback-success-base! [&::before]:border-1.5! [&::before]:border-solid!':
                    option.correct,
                },
              )}
              value={String(optionIndex)}
            />
            <div
              className={cn('relative w-full', {
                '[&_input]:pr-12': option.correct,
              })}
            >
              <TextInput
                className="[&_.cfa-input-label]:text-sm! [&_.cfa-input-label]:font-semibold! [&_input]:text-sm!"
                data-testid={`EnterAnswer${optionIndex + 1}`}
                errorMessage={
                  !validateTextField(option.answer[userLanguage] ?? '') &&
                  validationMode
                    ? t('TrainingPlans.buildQuizzes.errorQuestion')
                    : ''
                }
                label={`${t('TrainingPlans.buildQuizzes.option')} ${
                  optionIndex + 1
                }`}
                maxLength={Constants.FORMS.MAX_QUIZ_ANSWER_LENGTH}
                onChange={value => handleOptionText(value, optionIndex)}
                placeholder={t('TrainingPlans.buildQuizzes.enterAnswer')}
                isRequired
                value={option.answer[userLanguage] ?? ''}
              />
              {option.correct && (
                <div>
                  <IconCheck
                    size={20}
                    className="text-feedback-success-base! absolute! top-[calc(50%-10px)]! right-4! mt-2!"
                  />
                </div>
              )}
            </div>
            {(quiz.questions[questionIndex]?.answers.length ?? 0) > 2 && (
              <div className="mt-6 mb-0 ml-3 flex h-12 items-center">
                <IconX
                  onClick={() => deleteOption(optionIndex)}
                  size={26}
                  className="text-grayscale-gray6! cursor-pointer"
                />
              </div>
            )}
          </div>
        ))}
      </RadioGroup>
      {questionTemplateType?.isMultipleChoiceTemplate && (
        <Button
          className="text-brand-navy! dark:text-contrast! mt-2 ml-4! justify-start! gap-2! border-none! px-0! text-left! text-base! shadow-none!"
          color="secondary"
          onPress={addOption}
          size="lg"
          variant="text"
        >
          <IconPlus size={20} />
          {t('Button.addOption')}
        </Button>
      )}
    </div>
  );
};

export default BuildQuizzesAnswers;
